(function () {
  'use strict';

  var STORAGE_KEY = 'preferred_pickup_location_id';
  // Set when this visit fell back to the default branch (no choice, no
  // geolocation). Later pages then resolve at once instead of waiting for
  // geolocation again; a real choice or geolocation still overrides it.
  var DEFAULT_SESSION_KEY = 'bsf_default_this_visit';
  var GEO_TIMEOUT_MS = 5000;
  var COOKIE_KEY = 'preferred_pickup_location_id';
  var COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
  var EVENT_CHANGED = 'pickup-location:changed';
  var EVENT_READY = 'pickup-location:ready';

  function readCookie(name) {
    var match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  function writeCookie(name, value) {
    document.cookie =
      name + '=' + encodeURIComponent(value) +
      '; path=/; max-age=' + COOKIE_MAX_AGE + '; SameSite=Lax';
  }

  function readStored() {
    try {
      return localStorage.getItem(STORAGE_KEY) || readCookie(COOKIE_KEY);
    } catch (_) {
      return readCookie(COOKIE_KEY);
    }
  }

  function writeStored(id) {
    try { localStorage.setItem(STORAGE_KEY, id); } catch (_) {}
    writeCookie(COOKIE_KEY, id);
  }

  function haversineKm(lat1, lng1, lat2, lng2) {
    var R = 6371;
    var toRad = function (d) { return (d * Math.PI) / 180; };
    var dLat = toRad(lat2 - lat1);
    var dLng = toRad(lng2 - lng1);
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function hasCoords(loc) {
    return typeof loc.lat === 'number' && isFinite(loc.lat) &&
           typeof loc.lng === 'number' && isFinite(loc.lng);
  }

  // Branches without coordinates keep their payload order and sort after the
  // ranked ones. Feeding them to haversine yields NaN, which makes the whole
  // comparator non-deterministic and renders "NaN km" in the picker.
  function rankByDistance(locations, lat, lng) {
    var ranked = [], unranked = [];
    locations.forEach(function (loc) {
      if (hasCoords(loc)) {
        ranked.push(Object.assign({}, loc, { distanceKm: haversineKm(lat, lng, loc.lat, loc.lng) }));
      } else {
        unranked.push(Object.assign({}, loc));
      }
    });
    ranked.sort(function (a, b) { return a.distanceKm - b.distanceKm; });
    return ranked.concat(unranked);
  }

  function fetchLocations(url) {
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('locations payload http ' + r.status);
        return r.json();
      })
      .then(function (json) {
        if (!json || !Array.isArray(json.locations) || json.locations.length === 0) {
          throw new Error('locations payload empty');
        }
        return json;
      });
  }

  // Distance from the shopper to the nearest branch, when their position is
  // known (never asked for just for this). Kept for the tab session so every
  // page shows it without asking the browser again.
  var FAR_KEY = 'bsf_nearest_km';
  var EVENT_DISTANCE = 'pickup-location:distance';
  function recordNearestKm(lat, lng) {
    var ranked = rankByDistance(state.locations, lat, lng);
    var km = ranked[0] && ranked[0].distanceKm;
    if (!isFinite(km)) return;
    try { sessionStorage.setItem(FAR_KEY, String(km)); } catch (_) {}
    dispatch(EVENT_DISTANCE, { km: km });
  }
  function storedNearestKm() {
    try {
      var v = parseFloat(sessionStorage.getItem(FAR_KEY));
      return isFinite(v) ? v : null;
    } catch (_) { return null; }
  }
  // Only when the shopper already allowed location for this site: no prompt.
  function checkDistanceSilently() {
    if (storedNearestKm() !== null || !navigator.permissions || !navigator.permissions.query) return;
    navigator.permissions.query({ name: 'geolocation' }).then(function (status) {
      if (status.state === 'granted') getGeoPosition(GEO_TIMEOUT_MS);
    }).catch(function () {});
  }

  function getGeoPosition(timeoutMs) {
    return new Promise(function (resolve) {
      if (!navigator.geolocation) return resolve(null);
      var done = false;
      var timer = setTimeout(function () {
        if (!done) { done = true; resolve(null); }
      }, timeoutMs);
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          if (done) return;
          done = true;
          clearTimeout(timer);
          recordNearestKm(pos.coords.latitude, pos.coords.longitude);
          resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        function () {
          if (done) return;
          done = true;
          clearTimeout(timer);
          resolve(null);
        },
        { enableHighAccuracy: false, maximumAge: 5 * 60 * 1000, timeout: timeoutMs }
      );
    });
  }

  var IS_ARABIC = (document.documentElement.getAttribute('lang') || '').indexOf('ar') === 0;

  function applyToDocument(id) {
    if (document.body) document.body.setAttribute('data-preferred-pickup-id', id);
    document.querySelectorAll('.pickup-availability__line').forEach(function (line) {
      var lineId = line.getAttribute('data-location-id');
      line.classList.toggle('is-active', lineId === id);
      // Shopify location names are English only; the payload carries Arabic ones.
      if (IS_ARABIC) {
        var nameEl = line.querySelector('[data-pickup-store-name]');
        var loc = nameEl && api.findById(lineId);
        if (loc && loc.name_ar) nameEl.textContent = loc.name_ar;
      }
    });
  }

  function dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail }));
  }

  var state = {
    locations: [],
    currentId: null,
    // How currentId was decided: stored | geolocation | manual | default.
    // 'default' is the payload fallback, never the shopper's choice.
    source: null,
    ready: false
  };

  var api = {
    get: function () { return state.currentId; },
    source: function () { return state.source; },
    list: function () { return state.locations.slice(); },
    findById: function (id) {
      for (var i = 0; i < state.locations.length; i++) {
        if (state.locations[i].id === id) return state.locations[i];
      }
      return null;
    },
    set: function (id, opts) {
      opts = opts || {};
      var loc = api.findById(id);
      if (!loc) return false;
      var prev = state.currentId;
      state.currentId = id;
      state.source = 'manual';
      writeStored(id);
      applyToDocument(id);
      if (prev !== id) dispatch(EVENT_CHANGED, { id: id, location: loc, source: opts.source || 'manual' });
      return true;
    },
    /** km from the shopper to the nearest branch, or null when unknown. */
    nearestKm: function () { return storedNearestKm(); },
    detectNearest: function () {
      return getGeoPosition(GEO_TIMEOUT_MS).then(function (pos) {
        if (!pos) return null;
        var ranked = rankByDistance(state.locations, pos.lat, pos.lng);
        var best = ranked[0];
        return best && isFinite(best.distanceKm) ? best : null;
      });
    },
    nearestList: function (limit) {
      return getGeoPosition(GEO_TIMEOUT_MS).then(function (pos) {
        if (!pos) return state.locations.slice();
        var ranked = rankByDistance(state.locations, pos.lat, pos.lng);
        // Only truncate when the ranking is meaningful; otherwise an arbitrary
        // subset just hides branches the shopper may be standing next to.
        return isFinite((ranked[0] || {}).distanceKm)
          ? ranked.slice(0, limit || 5)
          : ranked;
      });
    }
  };

  function init() {
    var script = document.currentScript ||
      document.querySelector('script[data-pickup-location-init]');
    var payloadUrl = (script && script.getAttribute('data-locations-url')) || null;
    var autodetect = !script || script.getAttribute('data-autodetect') !== 'false';

    if (!payloadUrl) {
      console.warn('[pickup-location] missing data-locations-url on init script');
      return;
    }

    fetchLocations(payloadUrl)
      .then(function (payload) {
        var locations = payload.locations;
        state.locations = locations;
        // The branch to fall back on when the shopper has not chosen and cannot
        // be ranked. Named in the payload rather than assumed to be first in the
        // list, which is only alphabetical.
        var defaultId = payload.default_location_id && api.findById(payload.default_location_id)
          ? payload.default_location_id
          : locations[0].id;
        var stored = readStored();
        if (stored && api.findById(stored)) {
          state.currentId = stored;
          applyToDocument(stored);
          state.ready = true;
          state.source = 'stored';
          checkDistanceSilently(); dispatch(EVENT_READY, { id: stored, source: 'stored' });
          return;
        }
        var defaultThisVisit = false;
        try { defaultThisVisit = sessionStorage.getItem(DEFAULT_SESSION_KEY) === '1'; } catch (_) {}
        if (autodetect && !defaultThisVisit) {
          return api.detectNearest().then(function (nearest) {
            if (!nearest) {
              // Nothing rankable (no geolocation, or no branch has coordinates).
              // Fall back to the payload default rather than guessing by list
              // order. Not written to storage - this is not the shopper's choice,
              // so a later real pick still wins and the pill stays changeable.
              try { sessionStorage.setItem(DEFAULT_SESSION_KEY, '1'); } catch (_) {}
              state.currentId = defaultId;
              applyToDocument(defaultId);
              state.ready = true;
              state.source = 'default';
              checkDistanceSilently(); dispatch(EVENT_READY, { id: defaultId, source: 'default' });
              return;
            }
            state.currentId = nearest.id;
            writeStored(nearest.id);
            applyToDocument(nearest.id);
            state.ready = true;
            state.source = 'geolocation';
            checkDistanceSilently(); dispatch(EVENT_READY, { id: nearest.id, source: 'geolocation' });
          });
        }
        state.currentId = defaultId;
        applyToDocument(defaultId);
        state.ready = true;
        state.source = 'default';
        checkDistanceSilently(); dispatch(EVENT_READY, { id: defaultId, source: 'default' });
      })
      .catch(function (err) {
        console.warn('[pickup-location] init failed', err);
      });
  }

  window.PickupLocation = api;

  // A variant change re-renders the product page's pickup block from the
  // server, which drops the "is-active" mark (and the Arabic name) on the chosen
  // store's line, so the line disappeared after picking another size. Re-apply
  // whenever new pickup lines enter the page.
  if (window.MutationObserver) {
    new MutationObserver(function (mutations) {
      if (!state.currentId) return;
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType === 1 && (node.matches('.pickup-availability__line') || node.querySelector('.pickup-availability__line'))) {
            applyToDocument(state.currentId);
            return;
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
